import React, { useEffect, useRef, useState } from 'react';
import { apiGet } from '../../lib/api';
import { Search, Plus, User, X } from 'lucide-react';

/**
 * MultiContactPicker — pick multiple contacts, optionally scoped to a company.
 *
 * Props:
 *   value         array of contact_id strings
 *   companyId     scope contact list to this company (optional)
 *   onChange      (ids[]) => void
 *   onAddNew      () => void — called when user clicks "+ Add new contact" (parent opens drawer)
 *   placeholder
 *   testid
 */
export default function MultiContactPicker({ value = [], companyId, onChange, onAddNew, excludeIds = [], refreshSignal, placeholder = 'Search contacts to add…', testid = 'multi-contact-picker' }) {
  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const reload = () => {
    const url = companyId ? `/crm/contacts?company_id=${companyId}` : '/crm/contacts';
    apiGet(url).then((r) => setContacts(r.contacts || [])).catch(() => {});
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [companyId, refreshSignal]);

  useEffect(() => {
    const onClick = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const selectedContacts = contacts.filter((c) => value.includes(c.id));
  const q = query.trim().toLowerCase();
  const filtered = (!q ? contacts : contacts.filter((c) =>
    (c.name || '').toLowerCase().includes(q) ||
    (c.email || '').toLowerCase().includes(q) ||
    (c.designation || '').toLowerCase().includes(q)
  )).filter((c) => !value.includes(c.id) && !excludeIds.includes(c.id));

  const toggle = (id) => {
    if (value.includes(id)) onChange?.(value.filter((x) => x !== id));
    else onChange?.([...value, id]);
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid={testid}>
      {/* Selected chips */}
      {selectedContacts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedContacts.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 bg-indigo-50 text-[#3b4872] px-2 py-1 rounded-sm text-[11px] font-semibold" data-testid={`${testid}-chip-${c.id}`}>
              <User size={11} />
              {c.name}
              {c.designation && <span className="font-normal text-slate-500">· {c.designation}</span>}
              <button type="button" onClick={() => toggle(c.id)} className="hover:text-red-600">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="border border-slate-300 rounded-sm px-2.5 py-1.5 flex items-center gap-1.5 focus-within:border-[#3b4872] bg-white">
        <Search size={13} className="text-slate-400 shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="flex-1 text-sm border-0 focus:outline-none bg-transparent"
          data-testid={`${testid}-input`}
        />
        {onAddNew && (
          <button
            type="button"
            onClick={onAddNew}
            className="text-[10px] uppercase tracking-wider font-display font-bold text-emerald-700 hover:text-emerald-900 flex items-center gap-1 shrink-0"
            title="Open new contact drawer"
            data-testid={`${testid}-add-new`}
          >
            <Plus size={11} /> New
          </button>
        )}
      </div>
      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-30 max-h-60 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">
              {q ? 'No contacts match. ' : (companyId ? 'No more contacts for this company. ' : 'Start typing or ')}
              {onAddNew && (
                <button type="button" onClick={onAddNew} className="text-emerald-700 font-semibold underline">Add a new contact</button>
              )}
            </div>
          )}
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { toggle(c.id); setQuery(''); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 last:border-b-0"
              data-testid={`${testid}-opt-${c.id}`}
            >
              <User size={12} className="text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{c.name}</div>
                <div className="text-[10px] text-slate-500 truncate">{c.designation || c.email || '—'}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
